// ChartContainer.js 

import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { Line as LineChartJS, Bar as BarChartJS } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend } from "chart.js";
import { buildChartDataFromWholeData } from '../utils/transformApiResponse';
import { resolveIsBarChart } from '../utils/chartTypeUtils';
import '../utils/sharedStyles.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

// Threshold in pixels for "hovering on" the target line
const TARGET_LINE_HOVER_THRESHOLD = 12;

// Plugin: draw full-width target line and show tooltip when hovering on it
const targetLineFullWidthPlugin = {
  id: 'targetLineFullWidth',
  afterDatasetsDraw(chart) {
    const opts = chart.options.plugins?.targetLineFullWidth;
    if (!opts?.hasTarget || opts.targetValue == null || typeof opts.targetValue !== 'number') return;
    const ctx = chart.ctx;
    const yScale = chart.scales.y;
    const xScale = chart.scales.x;
    if (!yScale || !xScale) return;
    const y = yScale.getPixelForValue(opts.targetValue);
    ctx.save();
    ctx.strokeStyle = '#dc2626';
    ctx.setLineDash([6, 6]);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(xScale.left, y);
    ctx.lineTo(xScale.right, y);
    ctx.stroke();
    ctx.restore();
  },
  afterEvent(chart, args) {
    const event = args.event;
    if (event.type !== 'mousemove' && event.type !== 'mouseout') return;
    const opts = chart.options.plugins?.targetLineFullWidth;
    if (!opts?.hasTarget || opts.targetValue == null || typeof opts.targetValue !== 'number') return;
    const yScale = chart.scales.y;
    const xScale = chart.scales.x;
    if (!yScale || !xScale) return;
    const targetY = yScale.getPixelForValue(opts.targetValue);
    const cursorY = event.y;
    const inChartArea = event.x >= xScale.left && event.x <= xScale.right && event.y >= yScale.top && event.y <= yScale.bottom;
    const nearTargetLine = inChartArea && event.type !== 'mouseout' && Math.abs(cursorY - targetY) <= TARGET_LINE_HOVER_THRESHOLD;
    const targetDatasetIndex = chart.data.datasets.findIndex((d) => d.label === 'Target value');
    const hasTargetDataset = targetDatasetIndex >= 0 && chart.data.datasets[targetDatasetIndex].data?.length > 0;

    if (nearTargetLine && hasTargetDataset) {
      // Position tooltip at the start of the dashed line (left edge of chart), not at first data point
      const tooltipX = xScale.left + 8;
      const tooltipY = targetY;
      chart.tooltip.setActiveElements(
        [{ datasetIndex: targetDatasetIndex, index: 0 }],
        { x: tooltipX, y: tooltipY }
      );
      // Defer update so target line tooltip is shown
      if (chart._targetLineRafId != null) cancelAnimationFrame(chart._targetLineRafId);
      chart._targetLineRafId = requestAnimationFrame(() => {
        chart._targetLineRafId = null;
        if (chart.ctx) chart.update('none');
      });
    }
    // When not near target line, do nothing — let Chart.js default hover (bars, trend dots) control tooltip
  },
};
ChartJS.register(targetLineFullWidthPlugin);

// Register a single tooltip positioner: for "Target value" dataset, pin x to left edge; else use average
const tooltipPlugin = ChartJS.registry.getPlugin('tooltip');
if (tooltipPlugin?.positioners?.average) {
  tooltipPlugin.positioners.targetLineLeft = function (items, eventPosition) {
    const tooltip = this;
    const chart = tooltip.chart;
    const fallback = () => tooltipPlugin.positioners.average.call(tooltip, items, eventPosition);
    if (!chart?.scales?.x || !items?.length) return fallback();
    const first = items[0];
    if (chart.data.datasets[first?.datasetIndex]?.label !== 'Target value') return fallback();
    const yScale = chart.scales.y;
    if (!yScale) return fallback();
    const opts = chart.options.plugins?.targetLineFullWidth;
    const targetVal = opts?.targetValue;
    const y = typeof targetVal === 'number' && Number.isFinite(targetVal)
      ? yScale.getPixelForValue(targetVal)
      : (first.element?.y ?? first.y);
    const x = chart.scales.x.left + 10;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return fallback();
    return { x, y };
  };
}

// Color palette matching WordPress preview
const colorPalette = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
];

// Helper function to convert hex to rgba
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ChartComponent = ({ indicator, chartType, chartData, setChartRef, unit: unitOverride = null }) => {
  /** Visibility keyed by series label (stable across sort/filter). */
  const [seriesVisibility, setSeriesVisibility] = useState({});
  const chartCanvasRef = useRef(null);
  const chartContainerRef = useRef(null);
  /** Host for the canvas; used to defer Chart.js until the box has real size. */
  const chartHostRef = useRef(null);
  /**
   * Accordion bodies mount while collapsed (0×0 / display:none). Chart.js then
   * animates from the origin. Only mount the chart once the host is measurable.
   */
  const [chartReady, setChartReady] = useState(false);

  // Use chartData from API, or build from whole_data when payload had no datasets (e.g. legacy or fallback)
  const effectiveChartData = useMemo(() => {
    const hasPayload = chartData?.labels?.length > 0 && chartData?.datasets?.length > 0;
    if (hasPayload) return chartData;
    const whole = indicator?.acf?.graph_data_indicators?.whole_data || indicator?.acf?.whole_data || '';
    const built = whole ? buildChartDataFromWholeData(whole) : null;
    return built || chartData;
  }, [chartData, indicator?.acf?.graph_data_indicators?.whole_data, indicator?.acf?.whole_data]);

  // Check if we have chart data
  const hasChartData = effectiveChartData && 
                      effectiveChartData.labels && 
                      effectiveChartData.labels.length > 0 &&
                      effectiveChartData.datasets && 
                      effectiveChartData.datasets.length > 0;

  // Get target value from indicator
  const targetValue = indicator?.computed?.target?.value;
  const hasTarget = targetValue !== null && targetValue !== undefined && typeof targetValue === 'number';

  // D-26 / Pietro Jul 18 Q8: fixed horizontal axis 2015 → latest year in the series
  // (pad empty years so sparse Kosovo series sit in a full frame).
  const filteredChartData = useMemo(() => {
    if (!hasChartData || !effectiveChartData?.labels || !effectiveChartData?.datasets) {
      return null;
    }

    const minYear = 2015;
    const labels = effectiveChartData.labels || [];
    const yearIndex = new Map();
    labels.forEach((label, index) => {
      const year = typeof label === 'string' ? parseInt(label, 10) : Number(label);
      if (!Number.isNaN(year) && year >= minYear) {
        yearIndex.set(year, index);
      }
    });

    if (yearIndex.size === 0) {
      return null;
    }

    const maxYear = Math.max(...yearIndex.keys());
    const paddedYears = [];
    for (let y = minYear; y <= maxYear; y += 1) {
      paddedYears.push(y);
    }

    const filteredDatasets = effectiveChartData.datasets.map((dataset) => ({
      ...dataset,
      data: paddedYears.map((year) => {
        const idx = yearIndex.get(year);
        if (idx === undefined) return null;
        const v = dataset.data?.[idx];
        return v === undefined ? null : v;
      }),
    }));

    return {
      labels: paddedYears.map(String),
      datasets: filteredDatasets,
    };
  }, [hasChartData, effectiveChartData]);

  const wholeDataStr = indicator?.acf?.graph_data_indicators?.whole_data || indicator?.acf?.whole_data || '';
  const isBarChart = resolveIsBarChart(
    chartType === 'bar' || indicator?.chartType === 'bar',
    wholeDataStr,
    filteredChartData?.labels?.length
  );

  const countNumericPoints = (arr) => {
    let n = 0;
    for (let i = 0; i < (arr?.length || 0); i++) {
      const v = arr[i];
      if (typeof v === 'number' && Number.isFinite(v)) n++;
    }
    return n;
  };

  // Chart series list: same order for filters and Chart.js (headline / column 1 first).
  const chartSeriesList = useMemo(() => {
    if (!filteredChartData?.datasets) {
      return [];
    }
    const sortedDatasets = [...filteredChartData.datasets].sort((a, b) => {
      const aIdx = filteredChartData.datasets.indexOf(a);
      const bIdx = filteredChartData.datasets.indexOf(b);
      if (aIdx === 0 && bIdx !== 0) return -1;
      if (aIdx !== 0 && bIdx === 0) return 1;
      if (aIdx === 0 && bIdx === 0) return 0;
      return (a.label || '').localeCompare(b.label || '');
    });
    // D-26 pads 2015→latest, so label count is often >1 even when a series has
    // only one real observation (e.g. EU sdg_01_20a = 2018 only). Requiring ≥2
    // points after padding hid valid sparse bars/points — keep any series with
    // ≥1 numeric value; drop empty companion columns only.
    return sortedDatasets.filter((d) => countNumericPoints(d.data) >= 1);
  }, [filteredChartData]);

  // Prepare datasets for Chart.js - must be called before early return (React Hooks rule)
  const datasets = useMemo(() => {
    if (!chartSeriesList.length) {
      return [];
    }

    const preparedDatasets = chartSeriesList.map((dataset, index) => {
      const labelKey = dataset.label || `Series ${index + 1}`;
      const isVisible = seriesVisibility[labelKey] !== false;
      const color = colorPalette[index % colorPalette.length];
      
      const baseDataset = {
        label: labelKey,
        data: dataset.data || [],
        borderColor: color,
        backgroundColor: isBarChart ? (color + '33') : color, // 33 = 20% opacity in hex
        tension: isBarChart ? 0.25 : 0, // Straight lines for line charts
        fill: isBarChart ? true : false,
        hidden: !isVisible,
      };

      if (isBarChart) {
        return {
          ...baseDataset,
          type: 'bar',
        };
      } else {
        return {
          ...baseDataset,
          type: 'line',
          // Allow dots/line to draw in layout padding so top/edge points are not cut off
          clip: false,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
        };
      }
    });

    // Add target line and optional margin-of-error band if target value exists
    if (hasTarget && filteredChartData.labels && filteredChartData.labels.length > 0) {
      const targetData = new Array(filteredChartData.labels.length).fill(targetValue);
      // Margin of error band
      const moeEnabled = !!indicator?.moeEnabled && typeof indicator?.moePercent === 'number';
      if (moeEnabled) {
        const pct = Math.max(0, indicator.moePercent) / 100;
        const mode = (indicator.moeMode || 'around').toLowerCase();
        const lower = (mode === 'around' || mode === 'down') ? targetValue * (1 - pct) : targetValue;
        const upper = (mode === 'around' || mode === 'up') ? targetValue * (1 + pct) : targetValue;
        const lowerData = new Array(filteredChartData.labels.length).fill(lower);
        const upperData = new Array(filteredChartData.labels.length).fill(upper);
        const bandColor = 'rgba(220,38,38,0.12)'; // semi-transparent red
        // Lower invisible line
        preparedDatasets.push({
          label: 'Target (lower bound)',
          data: lowerData,
          borderColor: 'rgba(0,0,0,0)',
          backgroundColor: bandColor,
          borderWidth: 0,
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          fill: false,
        });
        // Upper line filled to previous -> creates band
        preparedDatasets.push({
          label: 'Target (upper bound)',
          data: upperData,
          borderColor: 'rgba(0,0,0,0)',
          backgroundColor: bandColor,
          borderWidth: 0,
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          fill: '-1',
        });
      }
      // Target line: drawn full-width by targetLineFullWidthPlugin; dataset kept for tooltip, hidden from normal draw
      const targetDataset = {
        label: 'Target value',
        data: targetData,
        borderColor: '#dc2626',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [6, 6],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        tension: 0,
        type: 'line',
        hidden: true, // Line is drawn full-width by plugin; this dataset only supplies tooltip
      };
      preparedDatasets.push(targetDataset);
    }

    return preparedDatasets;
  }, [chartSeriesList, seriesVisibility, isBarChart, hasTarget, targetValue, filteredChartData]);

  useEffect(() => {
    if (!filteredChartData) {
      if (setChartRef) setChartRef(null);
      return;
    }

    setSeriesVisibility((prev) => {
      const updated = { ...prev };
      chartSeriesList.forEach((dataset) => {
        const key = dataset.label || '';
        if (key && updated[key] === undefined) {
          updated[key] = true;
        }
      });
      return updated;
    });

    if (setChartRef && chartContainerRef.current) {
      setChartRef(chartContainerRef.current);
    }
  }, [filteredChartData, chartSeriesList, setChartRef]);

  // Defer Chart.js until the host has a real size (fixes accordion open animation).
  useLayoutEffect(() => {
    const el = chartHostRef.current;
    if (!el) {
      setChartReady(false);
      return undefined;
    }

    const syncReady = () => {
      const { width, height } = el.getBoundingClientRect();
      const next = width > 1 && height > 1;
      setChartReady((prev) => (prev === next ? prev : next));
    };

    syncReady();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncReady) : null;
    if (ro) ro.observe(el);

    return () => {
      if (ro) ro.disconnect();
    };
  }, [filteredChartData, isBarChart]);

  // Prefer per-graph unit (multi-unit EU) over the assessment/post-level unit.
  const displayUnit = (unitOverride != null && String(unitOverride).trim() !== '')
    ? String(unitOverride).trim()
    : (indicator?.unit != null ? String(indicator.unit).trim() : '');
  const isPercentUnit = !!(displayUnit && ((displayUnit === '%') || (displayUnit.toLowerCase() === 'percent') || (displayUnit.toLowerCase() === 'percentage')));
  // Min/max from source series only (not target/MoE helper datasets) for scale decisions
  const { minSeriesValueFromPayload, maxSeriesValueFromPayload } = useMemo(() => {
    if (!filteredChartData?.datasets) {
      return { minSeriesValueFromPayload: null, maxSeriesValueFromPayload: null };
    }
    let minVal = null;
    let maxVal = null;
    filteredChartData.datasets.forEach((d) => {
      (d.data || []).forEach((v) => {
        if (typeof v === 'number' && Number.isFinite(v)) {
          minVal = minVal === null ? v : Math.min(minVal, v);
          maxVal = maxVal === null ? v : Math.max(maxVal, v);
        }
      });
    });
    return { minSeriesValueFromPayload: minVal, maxSeriesValueFromPayload: maxVal };
  }, [filteredChartData]);
  // Fraction scale only when all key values look like proportions in 0..1 (avoid 1.5 threshold flipping 100% vs 1%)
  const targetLooksFraction = hasTarget && typeof targetValue === 'number' && Number.isFinite(targetValue) && targetValue > 0 && targetValue <= 1.001;
  const percentAsFraction = isPercentUnit
    && maxSeriesValueFromPayload !== null
    && maxSeriesValueFromPayload <= 1.001
    && (!hasTarget || targetLooksFraction);
  const validTargetForScale = hasTarget && typeof targetValue === 'number' && Number.isFinite(targetValue) && targetValue > 0;

  // Early return after all hooks
  if (!filteredChartData) {
    return null; // Don't render anything if no chart data
  }

  // Format numbers for axis/tooltip: avoid float noise; axis ticks use adaptive precision
  // so a tight scale (e.g. 0–0.01) does not collapse to duplicate "0" / "0.01" labels.
  const formatChartNumber = (value) => {
    if (value === null || value === undefined) return value;
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    return parseFloat(n.toFixed(2));
  };

  const formatAxisTick = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    if (n === 0) return '0';
    const abs = Math.abs(n);
    if (abs >= 100) return String(parseFloat(n.toFixed(0)));
    if (abs >= 10) return String(parseFloat(n.toFixed(1)));
    if (abs >= 1) return String(parseFloat(n.toFixed(2)));
    if (abs >= 0.1) return String(parseFloat(n.toFixed(2)));
    if (abs >= 0.01) return String(parseFloat(n.toFixed(3)));
    if (abs >= 0.001) return String(parseFloat(n.toFixed(4)));
    return String(parseFloat(n.toPrecision(2)));
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    // Room above the plot so line points at y-max are not clipped by the canvas edge
    layout: {
      padding: {
        top: 10,
        right: 6,
      },
    },
    animation: {
      duration: 750,
    },
    plugins: {
      targetLineFullWidth: {
        hasTarget: !!hasTarget,
        targetValue: hasTarget ? targetValue : null,
      },
      legend: {
        display: false, // We'll use custom filters
      },
      tooltip: {
        position: hasTarget ? 'targetLineLeft' : 'average',
        callbacks: {
          label: function(context) {
            const raw = context.parsed.y;
            if (raw === null || raw === undefined) return '';
            const val = typeof raw === 'number' ? raw : Number(raw);
            const num = Number.isFinite(val) ? formatChartNumber(val) : raw;
            const unit = displayUnit;
            if (unit && (String(unit) === '%' || String(unit).toLowerCase() === 'percent' || String(unit).toLowerCase() === 'percentage')) {
              const scaled = (percentAsFraction && typeof val === 'number') ? (val * 100) : num;
              return `${context.dataset.label || ''}: ${formatChartNumber(scaled)}%`;
            }
            const unitSuffix = unit ? ` ${unit}` : '';
            return `${context.dataset.label || ''}: ${num}${unitSuffix}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
        },
      },
      y: {
        beginAtZero: false,
        ...(isPercentUnit
          ? (percentAsFraction
              // Exact 0–1 (or target) scale; layout padding + clip:false keep points visible
              ? { min: 0, max: validTargetForScale ? Math.max(1, targetValue) : 1 }
              : (() => {
                  const cfgMax = typeof indicator?.yAxisMax === 'number' ? indicator.yAxisMax : null;
                  const dataMax = maxSeriesValueFromPayload != null && Number.isFinite(maxSeriesValueFromPayload)
                    ? maxSeriesValueFromPayload
                    : 0;
                  const tgt = validTargetForScale ? targetValue : 0;
                  const rawMax = Math.max(100, dataMax, tgt, cfgMax || 0);
                  return { min: 0, max: Number.isFinite(rawMax) ? rawMax : 100 };
                })())
          : (() => {
              // Non-% series: never force % scale from assessment unit; auto-scale to data.
              // Use Chart.js grace (not dataMax*1.05) so ticks stay on nice numbers (12, not 10.76).
              const yMinCfg = typeof indicator?.yAxisMin === 'number' ? indicator.yAxisMin : null;
              const yMaxCfg = typeof indicator?.yAxisMax === 'number' ? indicator.yAxisMax : null;
              const dataMin = minSeriesValueFromPayload != null && Number.isFinite(minSeriesValueFromPayload)
                ? minSeriesValueFromPayload
                : null;
              const dataMax = maxSeriesValueFromPayload != null && Number.isFinite(maxSeriesValueFromPayload)
                ? maxSeriesValueFromPayload
                : null;
              const min = yMinCfg !== null && Number.isFinite(yMinCfg)
                ? yMinCfg
                : (dataMin !== null && dataMin < 0 ? dataMin * 1.05 : 0);
              const out = {};
              if (Number.isFinite(min)) out.min = min;
              if (yMaxCfg !== null && Number.isFinite(yMaxCfg)) {
                out.max = yMaxCfg;
              } else {
                out.grace = '10%';
                // Include target in the auto range when it sits above the series
                if (validTargetForScale && (dataMax == null || targetValue > dataMax)) {
                  out.suggestedMax = targetValue;
                }
              }
              return out;
            })()),
        ticks: {
          callback: function(value) {
            if (isPercentUnit) {
              if (percentAsFraction) {
                return `${Math.round(Number(value) * 100)}%`;
              }
              return `${formatAxisTick(value)}%`;
            }
            return formatAxisTick(value);
          }
        }
      },
    },
  };

  const toggleSeries = (labelKey) => {
    setSeriesVisibility((prev) => ({
      ...prev,
      [labelKey]: prev[labelKey] === false ? true : false,
    }));
  };

  const showAll = () => {
    const allVisible = {};
    chartSeriesList.forEach((d) => {
      const key = d.label || '';
      if (key) allVisible[key] = true;
    });
    setSeriesVisibility(allVisible);
  };

  const hideAll = () => {
    const allHidden = {};
    chartSeriesList.forEach((d) => {
      const key = d.label || '';
      if (key) allHidden[key] = false;
    });
    setSeriesVisibility(allHidden);
  };

  return (
    <div ref={chartContainerRef} style={{ position: 'relative', marginTop: '20px' }}>
      {/* Filters */}
      <div id="sdg_preview_filters" className="sdg-preview-filters">
        {chartSeriesList.map((dataset, index) => {
          const labelKey = dataset.label || `Series ${index + 1}`;
          const isVisible = seriesVisibility[labelKey] !== false;
          const color = colorPalette[index % colorPalette.length];
          return (
            <label 
              key={labelKey}
              id={`chart-filter-${index}`}
              className={`chart-filter-label ${isVisible ? 'chart-filter-label-active' : 'chart-filter-label-inactive'}`}
              style={{ 
                border: `1px solid ${isVisible ? hexToRgba(color, 0.3) : '#e5e7eb'}`,
                backgroundColor: isVisible ? hexToRgba(color, 0.04) : '#f9fafb'
              }}
              onMouseEnter={(e) => {
                if (!isVisible) {
                  e.currentTarget.style.borderColor = hexToRgba(color, 0.2);
                  e.currentTarget.style.backgroundColor = hexToRgba(color, 0.03);
                } else {
                  e.currentTarget.style.borderColor = hexToRgba(color, 0.4);
                  e.currentTarget.style.backgroundColor = hexToRgba(color, 0.06);
                }
              }}
              onMouseLeave={(e) => {
                if (!isVisible) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                } else {
                  e.currentTarget.style.borderColor = hexToRgba(color, 0.3);
                  e.currentTarget.style.backgroundColor = hexToRgba(color, 0.04);
                }
              }}
            >
              <input
                type="checkbox"
                id={`chart-filter-checkbox-${index}`}
                className="chart-filter-checkbox"
                checked={isVisible}
                onChange={() => toggleSeries(labelKey)}
                style={{ 
                  accentColor: color
                }}
              />
              <span className="chart-filter-text">
                {labelKey}
              </span>
            </label>
          );
        })}
        {chartSeriesList.length > 1 && (
          <div className="chart-filter-actions" role="group" aria-label="Show or hide all series">
            <button
              type="button"
              id="chart-filter-button-all"
              className="chart-filter-button chart-filter-button-all"
              onClick={showAll}
            >
              All
            </button>
            <button
              type="button"
              id="chart-filter-button-none"
              className="chart-filter-button chart-filter-button-none"
              onClick={hideAll}
            >
              None
            </button>
          </div>
        )}
      </div>

      {/* Unit of measurement is shown in tooltip on hover of data points, not as separate text */}

      {/* Target is shown in Key values (key-value-item-value--target); chart keeps only the dashed line via plugin */}

      {/* Chart — host always mounted so ResizeObserver can detect accordion open */}
      <div ref={chartHostRef} style={{ margin: '8px 0', height: '260px', position: 'relative' }}>
        {chartReady && (isBarChart ? (
          <BarChartJS
            data={{
              labels: filteredChartData.labels || [],
              datasets: datasets,
            }}
            options={chartOptions}
            ref={(ref) => {
              if (ref) {
                if (chartCanvasRef.current !== ref.canvas) {
                  chartCanvasRef.current = ref.canvas;
                  if (ref.canvas) {
                    ref.canvas.id = 'sdg_preview_chart';
                  }
                }
              }
            }}
          />
        ) : (
          <LineChartJS
            data={{
              labels: filteredChartData.labels || [],
              datasets: datasets,
            }}
            options={chartOptions}
            ref={(ref) => {
              if (ref) {
                if (chartCanvasRef.current !== ref.canvas) {
                  chartCanvasRef.current = ref.canvas;
                  if (ref.canvas) {
                    ref.canvas.id = 'sdg_preview_chart';
                  }
                }
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ChartComponent;
